import { createContext, useEffect } from 'react';
import { getCookie } from 'cookies-next';
import useFetchConfig from 'lib/hooks/useFetchConfig';

import { recordsMatch, useSocket } from '@frontendmentorio/design-system-v2';

export const ReportContext = createContext();

export const ReportProvider = ({ children }) => {
  const reportsSocket = useSocket('/socket/v1/reports');
  const { mutate } = useFetchConfig();

  const handleReport = async ({ data: report }) => {
    await mutate(
      `/api/v3/solutions/${report.solution}/report`,
      { data: report },
      { revalidate: false }
    );
  };

  const generate = async (solution) => {
    const res = await fetch(`/api/v3/solutions/${solution.id}/report`, {
      method: 'POST',
    });

    if (!res.ok) {
      const body = await res.json();
      throw new Error(body.message);
    }

    await mutate(
      `/api/v3/solutions/${solution.id}/report`,
      async ({ data } = {}) => ({ data: { ...data, status: 'processing' } }),
      { revalidate: false }
    );
  };

  const recommend = async (report, type, issue) => {
    const res = await fetch(
      `/api/v3/solutions/${report.solution}/report/${type}/issues/${issue.id}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    if (!res.ok) {
      const body = await res.json();
      throw new Error(body.message);
    }

    mutate(
      `/api/v3/solutions/${report.solution}/report`,
      async ({ data } = {}) => {
        const issues = data[type].issues.map((item) => {
          return recordsMatch(item, issue)
            ? { ...item, recommendation: 'processing' }
            : item;
        });

        return { data: { ...data, [type]: { ...data[type], issues } } };
      },
      { revalidate: false }
    );
  };

  const watch = (solution) => {
    if (!reportsSocket.isConnected) {
      reportsSocket.connect({ token: getCookie('fem_token') });
    }

    reportsSocket.emit('solution:watch', { solution: solution.id });
  };

  const unwatch = (solution) => {
    if (!reportsSocket.isConnected) return;

    reportsSocket.emit('solution:unwatch', { solution: solution.id });
  };

  useEffect(() => {
    reportsSocket.on('report:processed', handleReport);

    return () => {
      reportsSocket.off('report:processed', handleReport);
    };
  }, []);

  return (
    <ReportContext.Provider value={{ generate, recommend, unwatch, watch }}>
      {children}
    </ReportContext.Provider>
  );
};
